import { Globe, Smartphone, Server, Cloud, Bot, Database } from 'lucide-react';
import { PillarPage } from './PillarPage';
import { ReactBadge, AppleBadge, AndroidBadge } from '../../components/PartnerBadges';

const services = [
  {
    icon: Globe,
    title: 'Custom Web Application Development',
    description:
      'Tailor-made web platforms built on modern stacks (React, Next.js, Node) — fast, scalable, and engineered around your exact business workflows.',
    highlights: [
      'Modern, performant React / Next.js front-ends',
      'Secure authentication & role-based access',
      'Pixel-perfect, responsive UI/UX',
      'CI/CD pipelines & automated testing',
    ],
  },
  {
    icon: Smartphone,
    title: 'iOS & Android Mobile Apps',
    description:
      'Native and cross-platform mobile applications — from delivery and booking to internal field tools — designed to feel native on every device.',
    highlights: [
      'iOS (Swift) & Android (Kotlin) builds',
      'Cross-platform with React Native / Flutter',
      'App Store & Play Store deployment',
      'Push notifications, offline mode, deep links',
    ],
  },
  {
    icon: Server,
    title: 'Backend APIs & Microservices',
    description:
      'Reliable backend systems that power your products — REST and GraphQL APIs, event-driven microservices, and admin dashboards built for scale.',
    highlights: [
      'REST & GraphQL API design',
      'Microservices & event-driven architecture',
      'Admin dashboards & internal tools',
      'Observability, logging & monitoring built-in',
    ],
  },
  {
    icon: Cloud,
    title: 'SaaS Product Engineering',
    description:
      'Take your idea from concept to live SaaS product — multi-tenant architecture, billing integration, customer onboarding, and analytics out of the box.',
    highlights: [
      'Multi-tenant architecture',
      'Stripe / billing & subscription flows',
      'Customer onboarding & in-app analytics',
      'Feature flags & gradual rollouts',
    ],
  },
  {
    icon: Bot,
    title: 'AI-Ready Systems & Automation',
    description:
      'Structure your data and workflows for AI — from automation bots and intelligent agents to RAG pipelines and ML-powered features in your products.',
    highlights: [
      'Workflow automation (n8n, Zapier, custom)',
      'LLM integrations & RAG pipelines',
      'Data engineering for AI workloads',
      'Custom intelligent agents & chatbots',
    ],
  },
  {
    icon: Database,
    title: 'Cloud Architecture (AWS · Azure · GCP)',
    description:
      'Cloud-native solutions designed for cost, performance, and resilience — provisioned with infrastructure-as-code and ready to scale.',
    highlights: [
      'AWS, Azure & GCP architecture',
      'Infrastructure-as-Code (Terraform)',
      'Containerization (Docker, Kubernetes)',
      'Cost optimization & performance tuning',
    ],
  },
];

const process = [
  { step: '01', label: 'Discover',  desc: 'We map the problem, validate scope, and align on success metrics.' },
  { step: '02', label: 'Design',    desc: 'Architecture, data models, and high-fidelity prototypes — reviewed before code.' },
  { step: '03', label: 'Build',     desc: 'Iterative sprints with continuous demos, testing, and stakeholder feedback.' },
  { step: '04', label: 'Launch',    desc: 'Production deployment, observability, and a smooth handover or rollout plan.' },
  { step: '05', label: 'Evolve',    desc: 'Ongoing support, feature releases, and performance optimization.' },
];

const faqs = [
  {
    question: 'What technologies do you use to build custom software?',
    answer: 'We work primarily with React, Next.js, and Node.js for web applications; Swift, Kotlin, React Native, and Flutter for mobile; Python, Go, and TypeScript for backend services. Our cloud stack covers AWS, Azure, and Google Cloud with Terraform for infrastructure-as-code.',
  },
  {
    question: 'How do you price custom software projects?',
    answer: 'We price based on scope, complexity, and team composition — not hourly rates. Every engagement starts with a discovery sprint to fully define requirements, after which we provide a fixed-price proposal. This protects you from scope creep and gives both sides a clear target.',
  },
  {
    question: 'How long does a typical software project take?',
    answer: 'A scoped MVP typically takes 8 to 16 weeks. Larger SaaS products and enterprise systems range from 4 to 9 months. We deliver in iterative sprints with continuous demos and stakeholder feedback throughout.',
  },
  {
    question: 'Can you build both iOS and Android apps?',
    answer: 'Yes. We build native iOS apps in Swift, native Android apps in Kotlin, and cross-platform apps using React Native or Flutter. We also handle the full App Store and Google Play submission and review process.',
  },
  {
    question: 'Should I build native apps or use React Native or Flutter?',
    answer: 'Native (Swift / Kotlin) wins when performance, deep OS integration, or platform-specific UX is critical — typically for camera, AR, gaming, or hardware-heavy apps. React Native and Flutter are the right call when you need a single codebase across iOS and Android and the UI is mostly standard. We help you decide based on your roadmap, not on what we feel like building.',
  },
  {
    question: 'Do you build SaaS products from scratch?',
    answer: 'Yes. We design and build SaaS products end-to-end including multi-tenant architecture, Stripe billing and subscription flows, customer onboarding, in-app analytics, feature flags, and admin dashboards.',
  },
  {
    question: 'How do you approach AI and automation projects?',
    answer: 'We build AI-ready systems including LLM integrations, RAG pipelines, intelligent agents, and workflow automation using tools like n8n and Zapier alongside custom-coded solutions. We also handle the data engineering required to make AI features reliable in production.',
  },
  {
    question: 'Which LLM providers do you work with?',
    answer: 'We integrate Claude (Anthropic), GPT (OpenAI), Gemini (Google), and open-weight models via AWS Bedrock, Azure OpenAI, and self-hosted inference (Ollama, vLLM). We help pick based on your use case — cost, context window, latency, data residency, and tool-use capability all factor in.',
  },
  {
    question: 'Do you handle hosting, deployment, and DevOps?',
    answer: 'Yes. We provision cloud infrastructure on AWS, Azure, or GCP using Terraform, set up CI/CD pipelines (GitHub Actions, GitLab CI), containerize with Docker and orchestrate with Kubernetes or managed services, and configure monitoring, logging, and alerting before launch.',
  },
  {
    question: 'How do you handle data security and compliance?',
    answer: 'We follow security-by-default — encryption at rest and in transit, secrets managed via cloud KMS or Vault, role-based access control, audit logging, and dependency scanning in CI. For regulated industries we build to SOC 2, GDPR, HIPAA, or PCI DSS requirements depending on your obligations.',
  },
  {
    question: 'Will I own the code and IP?',
    answer: 'Yes. All code is yours — pushed to a Git repository in your organization, with full documentation and architecture diagrams handed over at the end of every milestone. No license-back arrangements, no proprietary frameworks you can not maintain without us.',
  },
  {
    question: 'Do you offer maintenance and feature work after launch?',
    answer: 'Yes. We offer monthly retainers covering bug fixes, security patches, dependency upgrades, performance monitoring, and ongoing feature development with predictable SLAs and a dedicated Slack channel for your team.',
  },
];

export const SoftwareServicesPage = () => (
  <PillarPage
    meta={{
      title: 'Software & Mobile Development',
      label: 'Software & Mobile',
      headline: 'Custom Software, Built for Scale',
      italicWord: 'Built for Scale',
      description:
        'We build custom web apps, mobile products, SaaS platforms, and the backend systems that power them — engineered for performance, security, and AI-readiness from day one.',
      servicesHeading: 'Engineering Across the Full Stack',
    }}
    badgeRow={<><ReactBadge /><AppleBadge /><AndroidBadge /></>}
    theme={{
      primary: '#1E3A8A',
      accent:  '#3B82F6',
      bg:      '#EFF6FF',
      soft:    '#F0F6FF',
    }}
    services={services}
    process={process}
    cta={{
      headline: 'Have an idea or a system to modernize?',
      italic:   "Let's build it.",
      buttonLabel: 'Start Your Project',
    }}
    seo={{
      title: 'Custom Software, SaaS & Mobile App Development',
      description: 'Custom web apps, iOS and Android mobile apps, SaaS platforms, backend APIs, and AI-ready systems — built on React, Next.js, Swift, Kotlin, and modern cloud infrastructure.',
      path: '/services/software',
      serviceName: 'Custom Software & Mobile App Development',
    }}
    faqs={faqs}
  />
);
